import type { Timestamp } from '@bufbuild/protobuf/wkt';

function timeFormatString(target: number, single: string, multiple: string): string {
  if (target === 1) {
    return `${target} ${single}`;
  }

  return `${Math.floor(target)} ${multiple}`;
}

export const convertToDate = (
  date: Date | number | bigint | Timestamp,
  bigintUnit: 'ms' | 's' = 's',
): Date => {
  if (typeof date === 'number') {
    return new Date(date);
  }

  if (typeof date === 'object' && 'seconds' in date) {
    return new Date(Number(date.seconds) * 1000);
  }

  // we have to assume that the bigint is in seconds
  if (typeof date === 'bigint') {
    if (bigintUnit === 'ms') {
      return new Date(Number(date));
    }
    if (bigintUnit === 's') {
      return new Date(Number(date) * 1000);
    }
  }

  return date as Date;
};

export function timeSince(date: Date | number | bigint | Timestamp): string {
  if (date == null) {
    return '-';
  }

  const d = convertToDate(date);

  const seconds = Math.floor((new Date().getTime() - d.getTime()) / 1000);

  let interval = seconds / 31536000;

  if (interval > 1) {
    return `${timeFormatString(Math.floor(interval), 'year', 'years')} ago`;
  }
  interval = seconds / 2592000;
  if (interval > 1) {
    return `${timeFormatString(Math.floor(interval), 'month', 'months')} ago`;
  }
  interval = seconds / 86400;
  if (interval > 1) {
    return `${timeFormatString(Math.floor(interval), 'day', 'days')} ago`;
  }
  interval = seconds / 3600;
  if (interval > 1) {
    return `${timeFormatString(Math.floor(interval), 'hour', 'hours')} ago`;
  }
  interval = seconds / 60;
  if (interval > 1) {
    return `${timeFormatString(Math.floor(interval), 'minute', 'minutes')} ago`;
  }

  return `${timeFormatString(Math.floor(seconds), 'second', 'seconds')} ago`;
}

export function timeUntil(date: Date | number | bigint | Timestamp): string {
  const d = convertToDate(date);

  const seconds = Math.floor((d.getTime() - new Date().getTime()) / 1000);

  let interval = seconds / 31536000;
  if (interval > 1) {
    return `in ${timeFormatString(Math.floor(interval), 'year', 'years')}`;
  }
  interval = seconds / 2592000;
  if (interval > 1) {
    return `in ${timeFormatString(Math.floor(interval), 'month', 'months')}`;
  }
  interval = seconds / 86400;
  if (interval > 1) {
    return `in ${timeFormatString(Math.floor(interval), 'day', 'days')}`;
  }
  interval = seconds / 3600;
  if (interval > 1) {
    return `in ${timeFormatString(Math.floor(interval), 'hour', 'hours')}`;
  }
  interval = seconds / 60;
  if (interval > 1) {
    return `in ${timeFormatString(Math.floor(interval), 'minute', 'minutes')}`;
  }

  return `in ${timeFormatString(Math.floor(seconds), 'second', 'seconds')}`;
}

export default {};
